/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * NotificationDrawer Component
 *
 * Slide-out drawer displaying all unread notifications.
 * Supports client notifications (comments) and therapist notifications (shared notes, worksheets).
 * Includes "Mark all as read" functionality and empty state.
 *
 * Usage:
 * <NotificationDrawer
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   userRole="client" // or "therapist"
 * />
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/shared/ui/sheet";
import { Button } from "@/components/shared/ui/button";
import { Alert, AlertDescription } from "@/components/shared/ui/alert";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/shared/ui/tabs";
import { Loader2, Bell, CheckCheck } from "lucide-react";
import { NotificationItem } from "./NotificationItem";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useUnreadCommentCount } from "@/hooks/useUnreadCommentCount";
import { useTherapistUnreadNotes } from "@/hooks/useTherapistUnreadNotes";
import { useNotificationHistory } from "@/hooks/useNotificationHistory";
import { logger } from "@/lib/logger";
import { toast } from "sonner";

interface Notification {
  id: string;
  type: "comment" | "note" | "worksheet";
  taskId: string;
  taskTitle: string;
  journeyTitle?: string;
  preview: string;
  timestamp: string;
  authorName?: string;
  clientName?: string;
  clientId?: string;
  isPromptResponse?: boolean;
  actionType?: string;
  contentId?: string; // For worksheet auto-scroll (worksheet_response_id)
}

interface NotificationDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userRole: "client" | "therapist";
}

export const NotificationDrawer: React.FC<NotificationDrawerProps> = ({
  open,
  onOpenChange,
  userRole,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"unread" | "history">("unread");

  // Get appropriate hook based on user role
  const { markAllAsRead } = useUnreadCommentCount();
  const { markClientAsViewed } = useTherapistUnreadNotes();

  // Fetch notification history
  const { notifications: historyNotifications, isLoading: isHistoryLoading } =
    useNotificationHistory({
      userRole,
      enabled: open && activeTab === "history",
    });

  // Fetch notifications when drawer opens
  useEffect(() => {
    if (!open || !user) return;

    const fetchNotifications = async () => {
      setIsLoading(true);

      try {
        if (userRole === "client") {
          // Fetch client's unread comments
          const { data, error } = await supabase.rpc(
            "get_unread_notifications_for_digest",
            {
              target_user_id: user.id,
            }
          );

          if (error) throw error;

          // Map to notification format
          // FIXED: Cap at 100 notifications to prevent memory bloat
          const mappedNotifications: Notification[] = (data || [])
            .map((item: any) => ({
              id: item.comment_id,
              type: item.content_type === "worksheet" ? "worksheet" : "comment",
              taskId: item.task_id,
              taskTitle: item.task_title,
              preview: item.comment_text,
              timestamp: item.created_at,
              authorName: item.author_name,
              contentId: item.content_id, // Content ID for deep linking (note_id or worksheet_response_id)
            }))
            .slice(0, 100); // Hard limit to prevent memory growth

          setNotifications(mappedNotifications);
        } else {
          // Fetch therapist's unread notes
          const { data, error } = await supabase.rpc(
            "get_therapist_unread_notifications_detailed",
            {
              target_therapist_id: user.id,
            }
          );

          if (error) throw error;

          // Map to notification format (RPC now returns both 'note' and 'worksheet' types)
          // FIXED: Cap at 100 notifications to prevent memory bloat
          const mappedNotifications: Notification[] = (data || [])
            .map((item: any) => ({
              id: item.notification_id,
              type: item.notification_type as "note" | "worksheet",
              taskId: item.task_id,
              taskTitle: item.task_title,
              journeyTitle: item.journey_title,
              preview: item.preview_text,
              timestamp: item.updated_at,
              clientName: item.client_name,
              clientId: item.client_id,
              isPromptResponse: item.is_prompt_response,
              actionType: item.action_type,
              contentId: item.content_id, // Worksheet response ID or task note ID
            }))
            .slice(0, 100); // Hard limit to prevent memory growth

          setNotifications(mappedNotifications);
        }
      } catch (error) {
        logger.error(
          "[NotificationDrawer] Error fetching notifications:",
          error
        );
        toast.error(t("common.error", "Fejl"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t is stable, user object replaced by user?.id
  }, [open, user?.id, userRole]);

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Close drawer
    onOpenChange(false);

    // Mark as read/viewed
    if (userRole === "client") {
      // Mark individual comment as read
      try {
        const { error } = await supabase.rpc("mark_comment_read", {
          target_comment_id: notification.id,
        });
        if (error) {
          logger.error(
            "[NotificationDrawer] Error marking comment read:",
            error
          );
        } else {
          logger.info(
            "[NotificationDrawer] Comment marked as read:",
            notification.id
          );
        }
      } catch (error) {
        logger.error("[NotificationDrawer] Error marking comment read:", error);
      }
    } else {
      // Therapist notifications: distinguish between worksheet and note
      try {
        if (notification.type === "worksheet") {
          // Mark worksheet notification as read
          const { error } = await supabase.rpc(
            "mark_worksheet_notification_read",
            {
              target_notification_id: notification.id,
            }
          );
          if (error) {
            logger.error(
              "[NotificationDrawer] Error marking worksheet read:",
              error
            );
          } else {
            logger.info(
              "[NotificationDrawer] Worksheet marked as read:",
              notification.id
            );
          }
        } else {
          // Mark note as viewed by therapist
          const { error } = await supabase.rpc(
            "mark_note_viewed_by_therapist",
            {
              target_note_id: notification.id,
            }
          );
          if (error) {
            logger.error(
              "[NotificationDrawer] Error marking note viewed:",
              error
            );
          } else {
            logger.info(
              "[NotificationDrawer] Note marked as viewed:",
              notification.id
            );
          }
        }
      } catch (error) {
        logger.error("[NotificationDrawer] Error marking notification:", error);
      }
    }

    // Navigate to task
    const basePath =
      userRole === "client"
        ? "/dashboard/client/journey"
        : `/dashboard/therapist/client/${notification.clientId}/workspace?tab=conversations`; // Navigate to ClientWorkspace conversations tab

    // For clients: navigate to journey with task highlighted
    // For therapists: navigate to workspace conversations tab
    if (userRole === "client") {
      navigate(basePath, { state: { highlightTaskId: notification.taskId } });
    } else {
      // For therapists, just navigate to conversations tab - they'll see all conversations there
      navigate(basePath);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      if (userRole === "client") {
        await markAllAsRead();
      } else {
        // For therapists, mark all notifications as read
        // Handle both note notifications and worksheet notifications
        const noteNotifications = notifications.filter(
          (n) => n.type === "note"
        );
        const worksheetNotifications = notifications.filter(
          (n) => n.type === "worksheet"
        );

        // Mark all task note notifications as viewed (by unique client)
        if (noteNotifications.length > 0) {
          const uniqueClientIds = [
            ...new Set(
              noteNotifications
                .filter((n) => n.clientId)
                .map((n) => n.clientId as string)
            ),
          ];

          for (const clientId of uniqueClientIds) {
            await markClientAsViewed(clientId);
          }
        }

        // Mark all worksheet notifications as read (individually)
        if (worksheetNotifications.length > 0) {
          for (const notification of worksheetNotifications) {
            const { error } = await supabase.rpc(
              "mark_worksheet_notification_read",
              {
                target_notification_id: notification.id,
              }
            );

            if (error) {
              logger.error(
                "[NotificationDrawer] Error marking worksheet notification read:",
                error
              );
            }
          }
        }

        toast.success(t("notifications.allMarkedRead"));
      }

      // Clear local notifications
      setNotifications([]);
      onOpenChange(false);
    } catch (error) {
      logger.error("[NotificationDrawer] Error marking all as read:", error);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[90vw] max-w-[400px] sm:max-w-none sm:w-[400px] md:w-[540px] pb-safe-bottom px-safe !px-6"
      >
        <div className="h-full flex flex-col mt-6">
          {/* Header */}
          <SheetHeader className="space-y-2 pb-4 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                {t("notifications.notificationDrawerTitle", "Notifikationer")}
              </SheetTitle>
              {notifications.length > 0 && activeTab === "unread" && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className="text-xs"
                >
                  <CheckCheck className="h-4 w-4 mr-1" />
                  {t("notifications.markAllRead", "Marker alle")}
                </Button>
              )}
            </div>
            <SheetDescription>
              {activeTab === "unread"
                ? notifications.length > 0
                  ? userRole === "client"
                    ? t(
                        "notifications.clientNotificationsDesc",
                        "Du har {{count}} ulæste kommentarer",
                        { count: notifications.length }
                      )
                    : t(
                        "notifications.therapistNotificationsDesc",
                        "Du har {{count}} nye noter fra klienter",
                        { count: notifications.length }
                      )
                  : t(
                      "notifications.noNotifications",
                      "Ingen ulæste notifikationer"
                    )
                : t(
                    "notifications.historyDesc",
                    "Tidligere notifikationer fra de sidste 30 dage"
                  )}
            </SheetDescription>
          </SheetHeader>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onValueChange={(value) =>
              setActiveTab(value as "unread" | "history")
            }
            className="flex-1 flex flex-col mt-4"
          >
            <TabsList className="w-full grid grid-cols-2 mb-4">
              <TabsTrigger value="unread">
                {t("notifications.unread", "Ulæste")}
                {notifications.length > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-primary text-primary-foreground rounded-full text-xs">
                    {notifications.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="history">
                {t("notifications.history", "Historik")}
              </TabsTrigger>
            </TabsList>

            {/* Unread Tab Content */}
            <TabsContent value="unread" className="flex-1 overflow-y-auto mt-0">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : notifications.length === 0 ? (
                <Alert>
                  <Bell className="h-4 w-4" />
                  <AlertDescription>
                    {t(
                      "notifications.noNotifications",
                      "Ingen ulæste notifikationer"
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      id={notification.id}
                      type={notification.type}
                      taskTitle={notification.taskTitle}
                      preview={notification.preview}
                      timestamp={notification.timestamp}
                      authorName={notification.authorName}
                      clientName={notification.clientName}
                      isPromptResponse={notification.isPromptResponse}
                      actionType={notification.actionType}
                      onClick={() => handleNotificationClick(notification)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* History Tab Content */}
            <TabsContent
              value="history"
              className="flex-1 overflow-y-auto mt-0"
            >
              {isHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : historyNotifications.length === 0 ? (
                <Alert>
                  <Bell className="h-4 w-4" />
                  <AlertDescription>
                    {t(
                      "notifications.noHistory",
                      "Ingen notifikationer i historikken"
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  {historyNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      id={notification.id}
                      type={notification.type}
                      taskTitle={notification.taskTitle}
                      preview={notification.preview}
                      timestamp={notification.timestamp}
                      authorName={notification.authorName}
                      clientName={notification.clientName}
                      isPromptResponse={notification.isPromptResponse}
                      actionType={notification.actionType}
                      onClick={() => handleNotificationClick(notification)}
                      isRead={true}
                    />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};
